import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatXOF } from "../lib/api";
import { Button } from "../components/ui/button";
import { Plus, TrendingUp, Coins, Users, ArrowRight, Loader2 } from "lucide-react";
import CagnotteCard from "../components/CagnotteCard";
import { useAuth } from "../contexts/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_collected: 0, cagnottes_count: 0, contributions_count: 0 });
  const [cagnottes, setCagnottes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/cagnottes/mine"),
    ]).then(([s, c]) => {
      setStats(s.data);
      setCagnottes(c.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Tableau de bord</p>
          <h1 className="font-display text-4xl font-bold tracking-tight">Bonjour, {user?.name?.split(" ")[0] || "👋"}</h1>
        </div>
        <Link to="/cagnotte/new">
          <Button className="btn-gradient rounded-full h-11 px-6" data-testid="dashboard-create-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle cagnotte
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
        <div className="card-soft p-6" data-testid="stat-collected">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Total collecté</span>
            <Coins className="h-4 w-4 text-amber-500" />
          </div>
          <div className="text-3xl font-display font-bold gradient-text">{formatXOF(stats.total_collected)}</div>
        </div>
        <div className="card-soft p-6" data-testid="stat-cagnottes">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Cagnottes</span>
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="text-3xl font-display font-bold">{stats.cagnottes_count}</div>
        </div>
        <div className="card-soft p-6" data-testid="stat-contributions">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Contributions</span>
            <Users className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="text-3xl font-display font-bold">{stats.contributions_count}</div>
        </div>
      </div>

      {/* Cagnottes */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-semibold">Mes cagnottes</h2>
        <Link to="/explore" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center">
          Explorer <ArrowRight className="h-3 w-3 ml-1" />
        </Link>
      </div>

      {cagnottes.length === 0 ? (
        <div className="card-soft p-16 text-center">
          <div className="h-16 w-16 rounded-2xl gradient-gold-green flex items-center justify-center mx-auto mb-4">
            <Plus className="h-8 w-8 text-white" />
          </div>
          <h3 className="font-display text-xl font-semibold mb-2">Aucune cagnotte pour le moment</h3>
          <p className="text-muted-foreground mb-6">Créez votre première cagnotte en 2 minutes.</p>
          <Link to="/cagnotte/new">
            <Button className="btn-gradient rounded-full" data-testid="empty-create-btn">Créer ma première cagnotte</Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cagnottes.map((c) => <CagnotteCard key={c.id} cagnotte={c} />)}
        </div>
      )}
    </div>
  );
}
